import { GridActionsCellItem } from '@mui/x-data-grid/components';
import { GridColDef } from '@mui/x-data-grid/models/colDef/gridColDef';
import { Delete, Visibility } from '@mui/icons-material';
import { Tooltip, IconButton } from '@mui/material';
import { Link } from 'react-router-dom';
import { QuotationRequestResponse } from '@/api/bol';
import {
  formatDateTime,
  getEntityDisplayName,
  formatLocation,
  joinUniqueValues,
  formatExternalReferences,
  formatSpecialCharacteristics,
  sumValues,
  formatValueWithUnit,
  renderBooleanChip,
  joinArray,
  renderChip,
} from '@/utils/datagridFormatters';

interface QuotationRequestColumnsProps {
  onViewDetails?: (quotationRequestId: string) => void;
  onDelete: (quotationRequest: QuotationRequestResponse) => void;
  canDelete?: boolean;
  deleteTooltip?: string;
}

export const createQuotationRequestColumns = ({ onViewDetails: _onViewDetails, onDelete, canDelete = true, deleteTooltip = '' }: QuotationRequestColumnsProps): GridColDef[] => [
  {
    field: 'id',
    headerName: 'ID',
    flex: 2,
    minWidth: 200,
    valueGetter: (value, row) => {
      return row.id || '-';
    },
  },
  {
    field: 'submissionDateTime',
    headerName: 'Submission Date',
    flex: 1.5,
    minWidth: 180,
    valueGetter: (value, row) => {
      return formatDateTime(row.submissionDateTime);
    },
  },
  {
    field: 'requestor',
    headerName: 'Requestor',
    flex: 1.2,
    minWidth: 150,
    valueGetter: (value, row) => {
      return getEntityDisplayName(row.requestor);
    },
  },
  {
    field: 'startLocation',
    headerName: 'Origin',
    flex: 1.2,
    minWidth: 150,
    valueGetter: (value, row) => {
      return formatLocation(row.startLocation);
    },
  },
  {
    field: 'endLocation',
    headerName: 'Destination',
    flex: 1.2,
    minWidth: 150,
    valueGetter: (value, row) => {
      return formatLocation(row.endLocation);
    },
  },
  {
    field: 'preferredCurrency',
    headerName: 'Preferred Currency',
    flex: 0.8,
    minWidth: 100,
    valueGetter: (value, row) => {
      return row.preferredCurrency || '-';
    },
  },
  {
    field: 'batchesCount',
    headerName: 'Batches',
    flex: 0.8,
    minWidth: 100,
    valueGetter: (value, row) => {
      return row.batchesOfGoods?.length || 0;
    },
  },
  // New columns - hidden by default
  {
    field: 'quotationRequestStatus',
    headerName: 'Status',
    flex: 1.2,
    minWidth: 150,
    renderCell: (params) => renderChip('quotationRequestStatus', params.row.quotationRequestStatus),
  },
  {
    field: 'quotationProcess',
    headerName: 'Process',
    flex: 1,
    minWidth: 120,
    renderCell: (params) => renderChip('quotationProcess', params.row.quotationProcess, 'outlined'),
  },
  {
    field: 'incoterms',
    headerName: 'Incoterms',
    flex: 1.2,
    minWidth: 150,
    valueGetter: (value, row) => {
      return joinUniqueValues(row.incoterms);
    },
  },
  {
    field: 'externalReferences',
    headerName: 'External References',
    flex: 1.5,
    minWidth: 200,
    valueGetter: (value, row) => {
      return formatExternalReferences(row.externalReferences);
    },
  },
  {
    field: 'serviceOffersCount',
    headerName: 'Service Offers',
    flex: 0.8,
    minWidth: 100,
    valueGetter: (value, row) => {
      return row.serviceOffers?.length || 0;
    },
  },
  {
    field: 'serviceOfferNames',
    headerName: 'Offer Names',
    flex: 1.5,
    minWidth: 200,
    valueGetter: (value, row) => {
      const names = row.serviceOffers?.map((offer: { commercialProductName?: string }) => offer.commercialProductName).filter(Boolean);
      return joinArray(names);
    },
  },
  {
    field: 'serviceOfferProviders',
    headerName: 'Offer Providers',
    flex: 1.5,
    minWidth: 200,
    valueGetter: (value, row) => {
      const providers = row.serviceOffers?.map((offer: { provider?: { commercialName?: string | null; legalEntityName?: string | null; name?: string | null } | null }) => getEntityDisplayName(offer.provider));
      return joinUniqueValues(providers);
    },
  },
  {
    field: 'startLocationName',
    headerName: 'Origin Name',
    flex: 1.2,
    minWidth: 150,
    valueGetter: (value, row) => {
      return row.startLocation?.location?.locationName || '-';
    },
  },
  {
    field: 'startCountry',
    headerName: 'Origin Country',
    flex: 0.8,
    minWidth: 100,
    valueGetter: (value, row) => {
      const countryCode = row.startLocation?.location?.address?.addressCountry?.countryCode;
      return countryCode ? countryCode.toUpperCase() : '-';
    },
  },
  {
    field: 'startRequestedEarliest',
    headerName: 'Origin Window Start',
    flex: 1.3,
    minWidth: 180,
    valueGetter: (value, row) => {
      return formatDateTime(row.startLocation?.requestedStartTimeRange?.earliestDateTime);
    },
  },
  {
    field: 'startRequestedLatest',
    headerName: 'Origin Window End',
    flex: 1.3,
    minWidth: 180,
    valueGetter: (value, row) => {
      return formatDateTime(row.startLocation?.requestedEndTimeRange?.latestDateTime);
    },
  },
  {
    field: 'startAppointmentRequested',
    headerName: 'Origin Appointment',
    flex: 1,
    minWidth: 120,
    renderCell: (params) => renderBooleanChip(params.row.startLocation?.isAppointmentRequested),
  },
  {
    field: 'startActionTypes',
    headerName: 'Origin Actions',
    flex: 1.2,
    minWidth: 150,
    valueGetter: (value, row) => {
      const types = row.startLocation?.actions?.map((action: { actionType?: string }) => action.actionType).filter(Boolean);
      return joinUniqueValues(types);
    },
  },
  {
    field: 'endLocationName',
    headerName: 'Destination Name',
    flex: 1.2,
    minWidth: 150,
    valueGetter: (value, row) => {
      return row.endLocation?.location?.locationName || '-';
    },
  },
  {
    field: 'endCountry',
    headerName: 'Destination Country',
    flex: 0.8,
    minWidth: 100,
    valueGetter: (value, row) => {
      const countryCode = row.endLocation?.location?.address?.addressCountry?.countryCode;
      return countryCode ? countryCode.toUpperCase() : '-';
    },
  },
  {
    field: 'endRequestedEarliest',
    headerName: 'Destination Window Start',
    flex: 1.3,
    minWidth: 180,
    valueGetter: (value, row) => {
      return formatDateTime(row.endLocation?.requestedStartTimeRange?.earliestDateTime);
    },
  },
  {
    field: 'endRequestedLatest',
    headerName: 'Destination Window End',
    flex: 1.3,
    minWidth: 180,
    valueGetter: (value, row) => {
      return formatDateTime(row.endLocation?.requestedEndTimeRange?.latestDateTime);
    },
  },
  {
    field: 'endActionTypes',
    headerName: 'Destination Actions',
    flex: 1.2,
    minWidth: 150,
    valueGetter: (value, row) => {
      const types = row.endLocation?.actions?.map((action: { actionType?: string }) => action.actionType).filter(Boolean);
      return joinUniqueValues(types);
    },
  },
  {
    field: 'primaryProduct',
    headerName: 'Primary Product',
    flex: 1.5,
    minWidth: 200,
    valueGetter: (value, row) => {
      const firstBatch = row.batchesOfGoods?.[0];
      return firstBatch?.product?.commodityName || row.productDescription || '-';
    },
  },
  {
    field: 'containerTypes',
    headerName: 'Container Types',
    flex: 1.2,
    minWidth: 150,
    valueGetter: (value, row) => {
      const types = row.batchesOfGoods?.map((batch: { containerType?: string }) => batch.containerType).filter(Boolean);
      return joinUniqueValues(types);
    },
  },
  {
    field: 'totalQuantity',
    headerName: 'Total Quantity',
    flex: 1,
    minWidth: 120,
    valueGetter: (value, row) => {
      const total = sumValues(row.batchesOfGoods, (batch: { quantity?: { value?: number } }) => batch.quantity?.value);
      const unit = row.batchesOfGoods?.[0]?.quantity?.unit;
      return formatValueWithUnit(total || undefined, unit);
    },
  },
  {
    field: 'totalWeight',
    headerName: 'Total Weight',
    flex: 1,
    minWidth: 120,
    valueGetter: (value, row) => {
      const total = sumValues(row.batchesOfGoods, (batch: { weight?: { value?: number } }) => batch.weight?.value);
      const unit = row.batchesOfGoods?.[0]?.weight?.unit;
      return formatValueWithUnit(total || undefined, unit);
    },
  },
  {
    field: 'declaredValue',
    headerName: 'Declared Value',
    flex: 1,
    minWidth: 120,
    valueGetter: (value, row) => {
      const total = sumValues(row.batchesOfGoods, (batch: { declaredValue?: { value?: number } }) => batch.declaredValue?.value);
      const unit = row.batchesOfGoods?.[0]?.declaredValue?.unit || row.preferredCurrency;
      return formatValueWithUnit(total || undefined, unit);
    },
  },
  {
    field: 'contactsCount',
    headerName: 'Contacts',
    flex: 0.8,
    minWidth: 100,
    valueGetter: (value, row) => {
      return row.contacts?.length || 0;
    },
  },
  {
    field: 'contactRoles',
    headerName: 'Contact Roles',
    flex: 1.2,
    minWidth: 150,
    valueGetter: (value, row) => {
      const roles = row.contacts?.map((contact: { role?: string }) => contact.role).filter(Boolean);
      return joinUniqueValues(roles);
    },
  },
  {
    field: 'specialInstructionsCount',
    headerName: 'Special Instructions',
    flex: 1,
    minWidth: 120,
    valueGetter: (value, row) => {
      return row.specialInstructions?.length || 0;
    },
  },
  {
    field: 'specialCharacteristics',
    headerName: 'Special Characteristics',
    flex: 1.5,
    minWidth: 200,
    valueGetter: (value, row) => {
      return formatSpecialCharacteristics(row.specialCharacteristics);
    },
  },
  {
    field: 'quotationLink',
    headerName: 'Linked Quotation',
    flex: 1.5,
    minWidth: 200,
    valueGetter: (value, row) => {
      const link = row.quotation?.href || row.quotation;
      return link || '-';
    },
  },
  {
    field: 'actions',
    type: 'actions',
    headerName: 'Actions',
    flex: 1,
    minWidth: 120,
    hideable: false, // Always visible
    getActions: (params) => [
      <IconButton
        key="view"
        component={Link}
        to={`/quotation-requests/${params.row.id}`}
        size="small"
        color="primary"
        aria-label="View Details"
        sx={{ padding: '8px' }}
      >
        <Visibility fontSize="small" />
      </IconButton>,
      <Tooltip title={!canDelete ? deleteTooltip : ''} key="delete-tooltip">
        <span>
          <GridActionsCellItem
            icon={<Delete />}
            label="Delete"
            onClick={() => onDelete(params.row)}
            disabled={!canDelete}
            color="error"
          />
        </span>
      </Tooltip>,
    ],
  },
];

export const defaultQuotationRequestColumnVisibilityModel = {
  // Default visible columns
  id: true,
  submissionDateTime: true,
  requestor: true,
  startLocation: true,
  endLocation: true,
  preferredCurrency: true,
  batchesCount: true,
  actions: true,
  // Hidden by default
  quotationRequestStatus: false,
  quotationProcess: false,
  incoterms: false,
  externalReferences: false,
  serviceOffersCount: false,
  serviceOfferNames: false,
  serviceOfferProviders: false,
  startLocationName: false,
  startCountry: false,
  startRequestedEarliest: false,
  startRequestedLatest: false,
  startAppointmentRequested: false,
  startActionTypes: false,
  endLocationName: false,
  endCountry: false,
  endRequestedEarliest: false,
  endRequestedLatest: false,
  endActionTypes: false,
  primaryProduct: false,
  containerTypes: false,
  totalQuantity: false,
  totalWeight: false,
  declaredValue: false,
  contactsCount: false,
  contactRoles: false,
  documentsCount: false,
  specialInstructionsCount: false,
  specialCharacteristics: false,
  quotationLink: false,
};